'use client';

import { useEffect, useState } from 'react';
import { useAuthSession } from '@/lib/auth/auth-provider';
import { CamundaHistoryService } from '@/lib/camunda/history-service';
import type { HistoryItem } from '@/lib/camunda/types';
import type { SortConfig } from '@/stores/history-columns-store';

export const ITEMS_PER_PAGE = 50;

const SERVER_SORT_FIELD_MAP = new Map([
  ['processDefinitionName', 'definitionName'],
  ['startTime', 'startTime'],
  ['endTime', 'endTime'],
]);

export function isServerSortableField(field: string): boolean {
  return SERVER_SORT_FIELD_MAP.has(field);
}

function toErrorMessage(error: Error | null, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function useHistoryTableData(
  currentPage: number,
  sortConfig: SortConfig,
) {
  const { session, status: sessionStatus } = useAuthSession();
  const accessToken = session?.accessToken;
  const currentUserId = session?.preferredUsername?.trim();
  const sessionPending = sessionStatus === 'loading';
  const authenticationError = !sessionPending && (
    sessionStatus !== 'authenticated' || !accessToken
      ? 'No hay una sesión autenticada disponible'
      : !currentUserId
        ? 'El token de sesión no contiene la identidad de usuario esperada'
        : null
  );
  const [historyData, setHistoryData] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = () => {
    setReloadToken((current) => current + 1);
  };

  useEffect(() => {
    let cancelled = false;
    const fetchHistoryData = async () => {
      if (sessionPending) {
        setLoading(true);
        return;
      }

      if (authenticationError) {
        setHistoryData([]);
        setError(authenticationError);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const firstResult = (currentPage - 1) * ITEMS_PER_PAGE;
        const queryParams = {
          maxResults: ITEMS_PER_PAGE,
          firstResult,
          sortBy: SERVER_SORT_FIELD_MAP.get(sortConfig.field) ?? 'startTime',
          sortOrder: sortConfig.direction,
        };
        const data = await CamundaHistoryService.getHistoryTableDataOptimized(queryParams);
        if (cancelled) return;
        setHistoryData(data);
      } catch (fetchError) {
        console.error('Error fetching history data:', fetchError);
        if (!cancelled) {
          setHistoryData([]);
          setError(toErrorMessage(
            fetchError instanceof Error ? fetchError : null,
            'No se pudo cargar el historial',
          ));
        }
      }
      if (!cancelled) {
        setLoading(false);
      }
    };
    void fetchHistoryData();
    return () => {
      cancelled = true;
    };
  }, [authenticationError, currentPage, reloadToken, sessionPending, sortConfig]);

  useEffect(() => {
    let cancelled = false;
    const fetchTotalCount = async () => {
      if (sessionPending) {
        return;
      }

      if (authenticationError) {
        setTotalCount(0);
        return;
      }

      try {
        const count = await CamundaHistoryService.getHistoryTableDataCountOptimized({
          processDefinitionKey: undefined,
        });
        if (!cancelled) {
          setTotalCount(count);
        }
      } catch (fetchError) {
        console.error('Error fetching history count:', fetchError);
        if (!cancelled) {
          setError((current) =>
            current ?? toErrorMessage(
              fetchError instanceof Error ? fetchError : null,
              'No se pudo cargar el conteo del historial',
            ),
          );
        }
      }
    };
    void fetchTotalCount();
    return () => {
      cancelled = true;
    };
  }, [authenticationError, reloadToken, sessionPending]);

  return { historyData, loading, totalCount, error, reload };
}
